import { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, SkipForward, RefreshCw, Package, Clock, ImageOff, Sparkles, Undo2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ImageEditorModal } from '@/components/ImageEditorModal';
import { Badge } from '@/components/ui/Tag';
import { useBatchStore } from '@/store/useBatchStore';
import { useDataStore } from '@/store/useDataStore';
import { useConfigStore } from '@/store/useConfigStore';
import { generateImageWithFallback, getSelectedImageEditInstances } from '@/services/imageEditService';
import { uploadImageToStorage } from '@/services/storageService';
import { compressImageInBrowser, compressionMaxBytes } from '@/services/imageCompressionService';
import { parseImageUrls, joinImageUrls, buildSkuName } from '@/services/excelService';
import { cn } from '@/lib/utils';
import { EXCEL_FIELDS, FIRST_IMAGE_FIELD } from '@/lib/constants';
import type { StepProductResult, StepTaskResult, ExcelField, DataRow, ImageEditInstance } from '@/types';

// --- 差异检测 ---

function isCellDifferent(field: ExcelField, oldValue: string, newValue: string): boolean {
    if (field.isImage) {
        const oldUrls = parseImageUrls(oldValue);
        const newUrls = parseImageUrls(newValue);
        if (oldUrls.length !== newUrls.length) return true;
        return JSON.stringify(oldUrls) !== JSON.stringify(newUrls);
    }
    return oldValue !== newValue;
}

function aggregateImageUrls(rows: DataRow[], fieldKey: string): string[] {
    const allUrls: string[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
        const urls = parseImageUrls(row?.[fieldKey] ?? '');
        for (const url of urls) {
            if (!seen.has(url)) {
                seen.add(url);
                allUrls.push(url);
            }
        }
    }
    return allUrls;
}

function isFieldDifferentAcrossRows(field: ExcelField, beforeRows: DataRow[], afterRows: DataRow[]): boolean {
    if (field.isImage) {
        const oldUrls = aggregateImageUrls(beforeRows, field.key);
        const newUrls = aggregateImageUrls(afterRows, field.key);
        if (oldUrls.length !== newUrls.length) return true;
        return JSON.stringify(oldUrls) !== JSON.stringify(newUrls);
    }
    // 文本字段：任意一行不同即视为有差异（一个产品可能含多行 SKU，需逐行对比）
    return beforeRows.some((row, i) =>
        isCellDifferent(field, row?.[field.key] ?? '', afterRows[i]?.[field.key] ?? ''),
    );
}

function getImageDiff(oldValue: string, newValue: string, imageReplacements: Map<string, string>) {
    const oldUrls = parseImageUrls(oldValue);
    const newUrls = parseImageUrls(newValue);
    const oldSet = new Set(oldUrls);
    const newSet = new Set(newUrls);

    const reverseReplacements = new Map<string, string>();
    for (const [old, rep] of imageReplacements) {
        reverseReplacements.set(rep, old);
    }

    const replaced: Array<{ oldUrl: string; newUrl: string }> = [];
    const added: string[] = [];
    const removed: string[] = [];
    const common: string[] = [];

    const replacedOldSet = new Set<string>();
    for (const oldUrl of oldUrls) {
        const replacement = imageReplacements.get(oldUrl);
        if (replacement && newSet.has(replacement)) {
            replaced.push({ oldUrl, newUrl: replacement });
            replacedOldSet.add(oldUrl);
        } else if (newSet.has(oldUrl)) {
            common.push(oldUrl);
        } else {
            removed.push(oldUrl);
        }
    }

    const replacedNewSet = new Set(replaced.map(r => r.newUrl));
    for (const newUrl of newUrls) {
        if (replacedNewSet.has(newUrl)) continue;
        if (oldSet.has(newUrl)) continue;
        const oldFromReverse = reverseReplacements.get(newUrl);
        if (oldFromReverse && oldSet.has(oldFromReverse) && !replacedOldSet.has(oldFromReverse)) {
            replaced.push({ oldUrl: oldFromReverse, newUrl });
            replacedOldSet.add(oldFromReverse);
        } else {
            added.push(newUrl);
        }
    }

    return { added, removed, common, replaced };
}

// --- 缩略图 ---

function Thumb({ url, ring, onPreview, originalUrl, size = 'md' }: { url: string; ring?: string; onPreview: (url: string) => void; originalUrl?: string; size?: 'sm' | 'md' }) {
    const [err, setErr] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const hideTimer = useRef<number | null>(null);
    const dim = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
    if (err) return (
        <div className={cn('flex items-center justify-center rounded border border-zinc-200 bg-zinc-50', dim)}>
            <ImageOff size={12} className="text-zinc-300" />
        </div>
    );

    const showPreview = () => {
        if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
        const rect = imgRef.current?.getBoundingClientRect();
        if (rect) {
            const W = 312;
            let left = rect.right + 14;
            let top = rect.top;
            if (left + W > window.innerWidth) left = Math.max(12, rect.left - W - 14);
            if (top + W > window.innerHeight) top = Math.max(12, window.innerHeight - W - 12);
            setPos({ left, top });
        }
        setHovered(true);
    };
    const scheduleHide = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = window.setTimeout(() => setHovered(false), 120);
    };

    return (
        <>
            <img
                ref={imgRef}
                src={url}
                alt=""
                className={cn(dim, 'cursor-pointer rounded object-cover transition-transform duration-150 hover:scale-110', ring)}
                onClick={(e) => { e.stopPropagation(); onPreview(url); }}
                onError={() => setErr(true)}
                onMouseEnter={showPreview}
                onMouseLeave={scheduleHide}
            />
            {hovered && pos && createPortal(
                <div
                    className="pointer-events-auto fixed z-[300] cursor-zoom-in animate-fadeIn"
                    style={{ left: pos.left, top: pos.top }}
                    onClick={(e) => { e.stopPropagation(); onPreview(url); }}
                    onMouseEnter={showPreview}
                    onMouseLeave={scheduleHide}
                >
                    {originalUrl ? (
                        <div className="flex gap-2 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-2xl">
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-medium text-zinc-400">原图</span>
                                <img src={originalUrl} alt="" className="h-72 w-72 rounded-md border border-zinc-100 object-contain bg-zinc-50" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-medium text-amber-500">修改后</span>
                                <img src={url} alt="" className="h-72 w-72 rounded-md border border-amber-200 object-contain bg-zinc-50" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                        </div>
                    ) : (
                        <img src={url} alt="" className="h-80 w-80 rounded-lg border border-zinc-200 bg-white object-contain shadow-2xl ring-2 ring-white/80" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <div className="mt-1 flex justify-center">
                        <span className="rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">点击放大</span>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

// --- 字段对比行 ---

function FieldCompare({ field, beforeRows, afterRows, onPreview, imageReplacements }: {
    field: ExcelField;
    beforeRows: DataRow[];
    afterRows: DataRow[];
    onPreview: (url: string) => void;
    imageReplacements: Map<string, string>;
}) {
    if (field.isImage) {
        const original = joinImageUrls(aggregateImageUrls(beforeRows, field.key));
        const current = joinImageUrls(aggregateImageUrls(afterRows, field.key));
        if (!isCellDifferent(field, original, current)) return null;
        const diff = getImageDiff(original, current, imageReplacements);
        const originalUrls = parseImageUrls(original);
        const addedSet = new Set(diff.added);
        const removedSet = new Set(diff.removed);
        const replacedOldSet = new Set(diff.replaced.map(r => r.oldUrl));
        const replacedNewSet = new Set(diff.replaced.map(r => r.newUrl));
        const replacedMap = new Map(diff.replaced.map(r => [r.newUrl, r.oldUrl]));

        return (
            <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-x-3 border-b border-zinc-100 py-2 last:border-b-0">
                <span className="shrink-0 text-xs font-medium text-zinc-500">{field.label}</span>
                <div className="flex flex-wrap gap-1.5">
                    {originalUrls.length === 0 ? (
                        <span className="text-xs text-zinc-400">--</span>
                    ) : (
                        originalUrls.map((url, i) => {
                            let ring = 'ring-1 ring-zinc-200';
                            if (replacedOldSet.has(url)) ring = 'animate-ringBlink';
                            return <Thumb key={`o-${i}`} url={url} ring={ring} onPreview={onPreview} />;
                        })
                    )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {diff.replaced.map((r, i) => (
                        <Thumb key={`r-${i}`} url={r.newUrl} ring="animate-ringBlink" onPreview={onPreview} originalUrl={r.oldUrl} />
                    ))}
                    {addedSet.size > 0 && diff.added.map((url, i) => (
                        <Thumb key={`a-${i}`} url={url} ring="ring-1 ring-green-400" onPreview={onPreview} />
                    ))}
                    {removedSet.size > 0 && diff.removed.map((url, i) => (
                        <Thumb key={`d-${i}`} url={url} ring="ring-1 ring-red-400 opacity-50" onPreview={onPreview} />
                    ))}
                    {diff.common.map((url, i) => (
                        <Thumb key={`c-${i}`} url={url} ring="ring-1 ring-zinc-200" onPreview={onPreview} />
                    ))}
                </div>
            </div>
        );
    }

    // 文本字段：逐行对比（一个产品可能含多行 SKU，需一一对比）
    const rowDiffs = afterRows.map((row, i) => {
        const before = beforeRows[i]?.[field.key] ?? '';
        const after = row?.[field.key] ?? '';
        return { before, after, changed: isCellDifferent(field, before, after) };
    });
    const changedRows = rowDiffs.filter(r => r.changed);
    if (changedRows.length === 0) return null;

    // 产品级字段（如产品名称）在所有 SKU 行取值一致，聚合成单行对比，避免逐 SKU 重复展示
    const beforeUniform = beforeRows.length > 0 && beforeRows.every(r => (r?.[field.key] ?? '') === (beforeRows[0]?.[field.key] ?? ''));
    const afterUniform = afterRows.length > 0 && afterRows.every(r => (r?.[field.key] ?? '') === (afterRows[0]?.[field.key] ?? ''));
    const shouldAggregate = field.key === 'productName' || (beforeUniform && afterUniform);

    const skuNameFor = (i: number): string => {
        const row = afterRows[i] ?? beforeRows[i];
        return buildSkuName(row, i);
    };

    // 仅单行有变化（或产品级字段聚合）：沿用原有简洁对比
    if (changedRows.length === 1 || shouldAggregate) {
        const only = changedRows[0] ?? { before: beforeRows[0]?.[field.key] ?? '', after: afterRows[0]?.[field.key] ?? '' };
        const isDeleted = !only.after;
        return (
            <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-x-3 border-b border-zinc-100 py-1.5 last:border-b-0">
                <span className="shrink-0 text-xs font-medium text-zinc-500">{field.label}</span>
                <p className="truncate text-sm text-red-800 line-through decoration-red-300/60" title={only.before}>{only.before || '--'}</p>
                {isDeleted ? (
                    <p className="truncate text-sm text-zinc-400 line-through">{only.before || '--'}</p>
                ) : (
                    <p className="truncate text-sm font-medium text-green-800" title={only.after}>{only.after || '--'}</p>
                )}
            </div>
        );
    }

    // 多行（多个 SKU）各自不同：展示逐行对比列表
    return (
        <div className="border-b border-zinc-100 py-2 last:border-b-0">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-zinc-500">
                {field.label}
                <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-normal text-zinc-400">共 {changedRows.length} 个 SKU 不同</span>
            </div>
            <div className="space-y-1 rounded-lg bg-zinc-50/60 p-2">
                <div className="grid grid-cols-[auto_auto_1fr_1fr] gap-x-3 text-[9px] font-medium uppercase tracking-wider text-zinc-400">
                    <span></span>
                    <span>SKU</span>
                    <span>原始数据</span>
                    <span>修改后</span>
                </div>
                {rowDiffs.map((r, i) => {
                    if (!r.changed) return null;
                    const imgUrl = parseImageUrls(String((afterRows[i] ?? beforeRows[i])?.skuImage ?? ''))[0] || parseImageUrls(String((afterRows[i] ?? beforeRows[i])?.firstImage ?? ''))[0] || '';
                    return (
                        <div key={i} className="grid grid-cols-[auto_auto_1fr_1fr] items-center gap-x-3">
                            <div className="flex w-8 shrink-0 justify-center">
                                {imgUrl ? <Thumb url={imgUrl} size="sm" onPreview={onPreview} /> : <span className="text-[10px] text-zinc-300">—</span>}
                            </div>
                            <span className="max-w-[110px] truncate text-[11px] font-medium text-zinc-600" title={skuNameFor(i)}>{skuNameFor(i)}</span>
                            <p className="truncate text-xs text-red-800 line-through decoration-red-300/60" title={r.before}>{r.before || '--'}</p>
                            <p className="truncate text-xs font-medium text-green-800" title={r.after}>{r.after || '--'}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// --- 任务结果行 ---

const TASK_LABELS: Record<string, string> = {
    title: '优化标题',
    filter: '过滤低质量',
    size: '识别尺码图',
    aiImage: 'AI 编辑首图',
};

function TaskResultRow({
    task,
    processing,
    retrying,
    onRetry,
    onRollback,
}: {
    task: StepTaskResult;
    processing: boolean;
    retrying: boolean;
    onRetry: () => void;
    onRollback?: () => void;
}) {
    const StatusIcon = task.status === 'success' ? CheckCircle2 : task.status === 'failed' ? XCircle : SkipForward;
    const statusColor = task.status === 'success' ? 'text-green-500' : task.status === 'failed' ? 'text-red-500' : 'text-zinc-400';
    const badgeVariant = task.status === 'success' ? 'success' as const : task.status === 'failed' ? 'error' as const : 'default' as const;
    const durationSec = (task.durationMs / 1000).toFixed(1);

    return (
        <div className="flex items-center gap-2 rounded border border-zinc-150 bg-zinc-50/50 px-2.5 py-1.5">
            <StatusIcon size={14} className={statusColor} />
            <span className="text-xs font-medium text-zinc-700">{TASK_LABELS[task.taskType] ?? task.taskType}</span>
            <Badge variant={badgeVariant} className="text-[10px]">
                {task.status === 'success' ? '成功' : task.status === 'failed' ? '失败' : '跳过'}
            </Badge>
            <span className="flex items-center gap-0.5 text-[11px] text-zinc-400">
                <Clock size={10} />
                {durationSec}s
            </span>
            <span className="flex-1 truncate text-xs text-zinc-500" title={task.message}>{task.message}</span>
            {onRollback && (
                <button
                    onClick={onRollback}
                    disabled={processing}
                    className="flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <Undo2 size={11} />
                    回滚
                </button>
            )}
            <button
                onClick={onRetry}
                disabled={processing}
                className={cn(
                    'flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40',
                    retrying && 'border-indigo-200 text-indigo-600',
                )}
            >
                <RefreshCw size={11} className={cn(retrying && 'animate-spin')} />
                重新执行
            </button>
        </div>
    );
}

// --- 单产品结果卡片 ---

function ProductResultCard({
    result,
    compareFields,
    rows,
    originalRows,
    processing,
    retryingTasks,
    retryingAIImage,
    onRetry,
    onRollbackTask,
    onRetryAIImage,
    onRollbackAIImage,
    onPreview,
    even,
    imageReplacements,
}: {
    result: StepProductResult;
    compareFields: ExcelField[];
    rows: DataRow[];
    originalRows: DataRow[];
    processing: boolean;
    retryingTasks: Set<string>;
    retryingAIImage: Set<string>;
    onRetry: (productId: string, taskType: 'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight') => void;
    onRollbackTask: (productId: string, taskType: 'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight') => void;
    onRetryAIImage: (productId: string) => void;
    onRollbackAIImage: (productId: string) => void;
    onPreview: (url: string) => void;
    even: boolean;
    imageReplacements: Map<string, string>;
}) {
    const diffFields = useMemo(() => {
        return compareFields.filter((f) => {
            // 对 firstImage 使用 originalRows 对比（因为 AI 编辑可能在 snapshot 之前改了数据）
            if (f.key === 'firstImage' && imageReplacements.size > 0) {
                const origRows = originalRows.filter(r => r.productId === result.productId);
                return isFieldDifferentAcrossRows(f, origRows, result.snapshotAfter);
            }
            return isFieldDifferentAcrossRows(f, result.snapshotBefore, result.snapshotAfter);
        });
    }, [result, compareFields, originalRows, imageReplacements]);

    const firstImageUrl = useMemo(() => {
        const row = rows.find((r) => r.productId === result.productId);
        if (!row) return '';
        return row.firstImage ?? parseImageUrls(row.mainImages ?? '')[0] ?? '';
    }, [result.productId, rows]);

    const productName = useMemo(() => {
        const row = rows.find((r) => r.productId === result.productId);
        return row?.productName ?? '';
    }, [result.productId, rows]);

    const durationSec = (result.totalDurationMs / 1000).toFixed(1);

    // 任务成功/失败统计
    const successCount = result.taskResults.filter(t => t.status === 'success').length;
    const failedCount = result.taskResults.filter(t => t.status === 'failed').length;

    return (
        <div className={cn("rounded-lg border border-zinc-200", even && "bg-zinc-50/50")}>
            {/* 产品头 */}
            <div className={cn("flex items-center gap-2 px-3 py-2", even ? "bg-zinc-100/80" : "bg-zinc-50")}>
                <Package size={14} className="text-indigo-400" />
                {firstImageUrl && <img src={firstImageUrl} alt="" className="h-6 w-6 rounded border border-zinc-200 object-cover" />}
                <span className="font-mono text-xs text-zinc-500">{result.productId}</span>
                {productName && (
                    <>
                        <div className="mx-1 h-3 w-px bg-zinc-200" />
                        <span className="max-w-[180px] truncate text-sm text-zinc-600" title={productName}>{productName}</span>
                    </>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                    {failedCount > 0 && <Badge variant="error" className="text-[10px]">{failedCount}失败</Badge>}
                    {successCount > 0 && <Badge variant="success" className="text-[10px]">{successCount}成功</Badge>}
                    <span className="flex items-center gap-0.5 text-[11px] text-zinc-400">
                        <Clock size={10} />
                        {durationSec}s
                    </span>
                </div>
            </div>

            {/* 任务结果 */}
            <div className="border-t border-zinc-100 px-3 py-1.5">
                <div className="flex flex-col gap-1.5">
                    {result.taskResults.map((task) => (
                        <TaskResultRow
                            key={`${result.productId}-${task.taskType}`}
                            task={task}
                            processing={processing}
                            retrying={retryingTasks.has(`${result.productId}-${task.taskType}`)}
                            onRetry={() => onRetry(result.productId, task.taskType)}
                            onRollback={() => onRollbackTask(result.productId, task.taskType)}
                        />
                    ))}
                    {/* AI 编辑首图结果行 */}
                    {(() => {
                        const productRows = rows.filter(r => r.productId === result.productId);
                        const currentFirst = productRows[0]?.firstImage ?? parseImageUrls(productRows[0]?.mainImages ?? '')[0] ?? '';
                        const wasReplaced = currentFirst && imageReplacements.size > 0 && Array.from(imageReplacements.values()).includes(currentFirst);
                        if (!wasReplaced) return null;
                        return (
                            <div className="flex items-center gap-2 rounded border border-violet-150 bg-violet-50/30 px-2.5 py-1.5">
                                <Sparkles size={14} className="text-violet-500" />
                                <span className="text-xs font-medium text-zinc-700">AI 编辑首图</span>
                                <Badge variant="success" className="text-[10px]">成功</Badge>
                                <span className="flex-1" />
                                <button
                                    onClick={() => onRollbackAIImage(result.productId)}
                                    disabled={processing}
                                    className="flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <Undo2 size={11} />
                                    回滚
                                </button>
                                <button
                                    onClick={() => onRetryAIImage(result.productId)}
                                    disabled={processing}
                                    className="flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-0.5 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <RefreshCw size={11} className={cn(retryingAIImage.has(result.productId) && 'animate-spin')} />
                                    重新执行
                                </button>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* 前后对比 */}
            {diffFields.length > 0 && (
                <div className="border-t border-zinc-100 px-3 py-1.5">
                    <div className="rounded-lg border border-zinc-200">
                        <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 border-b border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                            <span className="w-14">字段</span>
                            <span>原始数据</span>
                            <span>修改后</span>
                        </div>
                        <div className="px-3 py-1">
                            {diffFields.map((field) => {
                                // 对 firstImage 使用 originalRows 的原始数据
                                const useOriginalForBefore = field.key === 'firstImage' && imageReplacements.size > 0;
                                const beforeRows = useOriginalForBefore
                                    ? originalRows.filter(r => r.productId === result.productId)
                                    : result.snapshotBefore;
                                return (
                                    <FieldCompare
                                        key={field.key}
                                        field={field}
                                        beforeRows={beforeRows}
                                        afterRows={result.snapshotAfter}
                                        onPreview={onPreview}
                                        imageReplacements={imageReplacements}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- 主组件 ---

interface StepConfirmModalProps {
    open: boolean;
}

export function StepConfirmModal({ open }: StepConfirmModalProps) {
    const stepState = useBatchStore((s) => s.stepState);
    const retryStepTask = useBatchStore((s) => s.retryStepTask);
    const confirmStepContinue = useBatchStore((s) => s.confirmStepContinue);
    const cancelStepRemaining = useBatchStore((s) => s.cancelStepRemaining);

    const rows = useDataStore((s) => s.rows);
    const originalRows = useDataStore((s) => s.originalRows);
    const imageReplacements = useDataStore((s) => s.imageReplacements);

    const { imageEditInstances, imageEditSettings } = useConfigStore();
    const { updateRow, recordImageReplacement } = useDataStore();

    const [retryingTasks, setRetryingTasks] = useState<Set<string>>(new Set());
    const [retryingAIImage, setRetryingAIImage] = useState<Set<string>>(new Set());
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState('');

    const completedResults = stepState.completedResults;

    const compareFields = useMemo(() => {
        return [...EXCEL_FIELDS, FIRST_IMAGE_FIELD];
    }, []);

    const handleRollbackTask = (productId: string, taskType: 'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight') => {
        const result = completedResults.find(r => r.productId === productId);
        if (!result) return;
        const store = useDataStore.getState();

        // 用 snapshotBefore 和 originalIndices 回滚该产品的所有行
        for (let i = 0; i < result.originalIndices.length; i++) {
            const originalGlobalIndex = result.originalIndices[i];
            const snapshotRow = result.snapshotBefore[i];
            // Ensure the row still exists at the original index before updating
            if (store.rows[originalGlobalIndex] && snapshotRow) {
                store.updateRow(originalGlobalIndex, snapshotRow);
            }
        }
    };

    const handleRollbackAIImage = (productId: string) => {
        const store = useDataStore.getState();
        const productIndices: number[] = [];
        for (let i = 0; i < store.rows.length; i++) {
            if (store.rows[i].productId === productId) productIndices.push(i);
        }
        // 用 originalRows 回滚该产品的行（恢复首图）
        for (const idx of productIndices) {
            const original = store.originalRows[idx];
            if (original) {
                // 只回滚 firstImage 和 mainImages
                const current = store.rows[idx];
                store.updateRow(idx, { ...current, firstImage: original.firstImage, mainImages: original.mainImages });
            }
        }
        // 清除该产品相关的 imageReplacements
        const { imageReplacements } = store;
        const next = new Map(imageReplacements);
        for (const [oldUrl, newUrl] of imageReplacements) {
            // 检查是否是该产品的替换
            const productRows = store.rows.filter(r => r.productId === productId);
            for (const row of productRows) {
                const origRow = store.originalRows[store.rows.indexOf(row)];
                if (origRow && (origRow.firstImage === oldUrl || parseImageUrls(origRow.mainImages ?? '').includes(oldUrl))) {
                    next.delete(oldUrl);
                }
            }
        }
        useDataStore.setState({ imageReplacements: next });
    };

    const handleRetry = async (productId: string, taskType: 'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight') => {
        const key = `${productId}-${taskType}`;
        setRetryingTasks((prev) => new Set(prev).add(key));
        try {
            await retryStepTask(taskType, productId);
        } finally {
            setRetryingTasks((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const handleRetryAIImage = async (productId: string) => {
        setRetryingAIImage((prev) => new Set(prev).add(productId));
        try {
            const instances = getSelectedImageEditInstances(imageEditInstances);
            const instance = instances[0];
            if (!instance) return;
            const store = useDataStore.getState();
            const productRows = store.rows.filter(r => r.productId === productId);
            if (productRows.length === 0) return;

            // 获取当前首图 URL
            const firstImg = productRows[0].firstImage || parseImageUrls(productRows[0].mainImages ?? '')[0] || '';
            if (!firstImg) return;

            // 下载原图
            const proxyUrl = firstImg.startsWith('http') ? `/api/proxy?target=${encodeURIComponent(firstImg)}` : firstImg;
            const resp = await fetch(proxyUrl);
            const blob = await resp.blob();
            const file = new File([blob], 'firstImage.png', { type: blob.type || 'image/png' });

            const prompt = imageEditSettings.presetPrompts?.[0] ?? '主体保持不变，文字保持不变，背景替换为更适合产品使用的场景';
            const generated = await generateImageWithFallback(instances, {
                prompt,
                images: [file],
                ratio: imageEditSettings.defaultRatio,
                quality: imageEditSettings.defaultQuality,
                count: imageEditSettings.defaultCount,
            });
            const { job, instance: usedInstance } = generated;

            const resultItem = job.result?.data?.[0] ?? job.result?.images?.[0];
            if (!resultItem) return;
            const resultSource = resultItem.b64_json
                ? `data:image/png;base64,${resultItem.b64_json}`
                : resultItem.url
                    ? (resultItem.url.startsWith('http')
                        ? resultItem.url
                        : `${usedInstance.apiUrl.replace(/\/$/, '')}${resultItem.url.startsWith('/') ? '' : '/'}${resultItem.url}`)
                    : '';
            if (!resultSource) return;

            let finalUrl: string;
            if (imageEditSettings.browserCompressionEnabled) {
                const compressed = await compressImageInBrowser(resultSource, compressionMaxBytes(imageEditSettings.compressionLevel));
                finalUrl = imageEditSettings.jdcloudStorageEnabled
                    ? await uploadImageToStorage({
                        fileName: compressed.fileName,
                        fileBase64: compressed.fileBase64,
                        contentType: compressed.contentType,
                    })
                    : compressed.dataUrl;
            } else if (imageEditSettings.jdcloudStorageEnabled) {
                finalUrl = resultItem.b64_json
                    ? await uploadImageToStorage({
                        fileName: `ai-image-${Date.now()}.png`,
                        fileBase64: resultItem.b64_json,
                        contentType: 'image/png',
                    })
                    : await uploadImageToStorage({ imageUrl: resultSource });
            } else {
                finalUrl = resultSource;
            }

            // 替换该产品所有行的首图和主图
            for (const row of productRows) {
                const idx = store.rows.indexOf(row);
                if (idx === -1) continue;
                const updated = { ...row };
                if (updated.firstImage) {
                    recordImageReplacement(updated.firstImage, finalUrl);
                    updated.firstImage = finalUrl;
                }
                if (updated.mainImages) {
                    const imgs = parseImageUrls(updated.mainImages);
                    if (imgs.length > 0) {
                        recordImageReplacement(imgs[0], finalUrl);
                        updated.mainImages = joinImageUrls([finalUrl, ...imgs.slice(1)]);
                    }
                }
                updateRow(idx, updated);
            }
        } finally {
            setRetryingAIImage((prev) => {
                const next = new Set(prev);
                next.delete(productId);
                return next;
            });
        }
    };

    // 汇总统计
    const stats = useMemo(() => {
        let totalSuccess = 0;
        let totalFailed = 0;
        let totalSkipped = 0;
        let totalDurationMs = 0;
        for (const result of completedResults) {
            totalDurationMs += result.totalDurationMs;
            for (const task of result.taskResults) {
                if (task.status === 'success') totalSuccess++;
                else if (task.status === 'failed') totalFailed++;
                else totalSkipped++;
            }
        }
        return { totalSuccess, totalFailed, totalSkipped, totalDurationMs };
    }, [completedResults]);

    return (
        <>
            <Modal open={open} title="步进确认" width="full" className="h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)]">
                {completedResults.length > 0 ? (
                    <div className="flex min-h-0 flex-1 flex-col">
                        {/* 统计栏 */}
                        <div className="flex shrink-0 items-center gap-2 border-b border-zinc-200 pb-2 mb-2">
                            <Package size={16} className="text-indigo-500" />
                            <span className="text-sm font-medium text-zinc-700">
                                全部 {completedResults.length} 个产品已处理完毕
                            </span>
                            <div className="mx-1 h-3 w-px bg-zinc-200" />
                            {stats.totalSuccess > 0 && <Badge variant="success" className="text-xs">{stats.totalSuccess} 成功</Badge>}
                            {stats.totalFailed > 0 && <Badge variant="error" className="text-xs">{stats.totalFailed} 失败</Badge>}
                            {stats.totalSkipped > 0 && <Badge variant="default" className="text-xs">{stats.totalSkipped} 跳过</Badge>}
                            <span className="flex items-center gap-0.5 text-xs text-zinc-400">
                                <Clock size={11} />
                                {(stats.totalDurationMs / 1000).toFixed(1)}s
                            </span>
                            <div className="ml-auto flex items-center gap-2 text-[11px] text-zinc-400">
                                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-red-200" />原始</span>
                                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-green-200" />修改后</span>
                            </div>
                        </div>

                        {/* 产品结果列表 */}
                        <div className="flex-1 overflow-auto">
                            <div className="flex flex-col gap-2.5 p-1.5">
                                {completedResults.map((result, idx) => (
                                    <ProductResultCard
                                        key={result.productId}
                                        result={result}
                                        compareFields={compareFields}
                                        rows={rows}
                                        originalRows={originalRows}
                                        processing={stepState.processing}
                                        retryingTasks={retryingTasks}
                                        retryingAIImage={retryingAIImage}
                                        onRetry={handleRetry}
                                        onRollbackTask={handleRollbackTask}
                                        onRetryAIImage={handleRetryAIImage}
                                        onRollbackAIImage={handleRollbackAIImage}
                                        onPreview={(url) => { setEditorImageUrl(url); setEditorOpen(true); }}
                                        even={idx % 2 === 1}
                                        imageReplacements={imageReplacements}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* 底部操作 */}
                        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-3 mt-2">
                            <button
                                onClick={cancelStepRemaining}
                                className="rounded-md border border-zinc-300 px-4 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                            >
                                撤销所有修改
                            </button>
                            <button
                                onClick={confirmStepContinue}
                                className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                            >
                                {stepState.currentProductIndex + 1 < stepState.totalProducts ? '确认并继续' : '确认'}
                            </button>
                        </div>
                    </div>
                ) : null}
            </Modal>
            <ImageEditorModal
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                imageUrl={editorImageUrl}
                readOnly
            />
        </>
    );
}
